import { SelectionModel } from '@angular/cdk/collections';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DataBuild } from 'src/app/models/data-build';
import { DataBuildsService } from 'src/app/services/data-builds.service';
import { Host } from 'src/app/models/host';
import { HostsService } from 'src/app/services/hosts.service';
import { IdentityService } from 'src/app/services/identity.service';
import { TeamsService } from 'src/app/services/teams.service';
import { MenuService } from 'src/app/services/menu.service';
import { OmnibarComponent } from '../../omnibar/omnibar.component';
import { SocketService } from 'src/app/services/socket.service';
import { SubscriptionContainer } from 'src/app/models/subscription-container';
import { debounce } from 'src/app/directives/debounce.decorator';
import { DialogService } from '../../../services/dialog.service';

@Component({
    selector: 'app-builds',
    templateUrl: './builds.component.html',
    styleUrls: ['./builds.component.scss'],
    standalone: false
})
export class BuildsComponent implements OnInit, OnDestroy {
  private dialogs = inject(DialogService);
  @ViewChild(MatTable) table: MatTable<any>;
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(OmnibarComponent) omnibar: OmnibarComponent;

  @Input() teamName: string;
  @Input() isWidget: boolean = false;
  @Output() onBuildClick = new EventEmitter<{ rdsName: string; runid: number; user: string; isArchived: boolean }>();
  @Output() onAddClick = new EventEmitter();
  @Output() onCloneClick = new EventEmitter<{ rdsName: string; runid: number; isArchived: boolean }>();
  @Output() onDiffClick = new EventEmitter<{ left: DataBuild; right: DataBuild }>();

  private subSort: any = null;
  private lastClickedRow: DataBuild = null;
  public pageSize = 10;

  public isLoading: boolean = true;
  public availableColumns: string[] = [
    'select',
    'isRunning',
    'status',
    'rdsName',
    'runid',
    'dataset',
    'host',
    'user',
    'startTime',
    'endTime',
    'lastModified',
    'duration',
    'errors',
    'tags'
  ];
  public displayedColumns: string[] = [
    'select',
    'isRunning',
    'status',
    'rdsName',
    'runid',
    'dataset',
    'user',
    'startTime',
    'duration',
    'errors',
    'tags'
  ];
  public dataSource = new MatTableDataSource<DataBuild>([]);
  public selection = new SelectionModel<DataBuild>(true, []);
  public originalData: DataBuild[] = [];
  private knownHosts: { [key: string]: Host } = {};
  private _subs = new SubscriptionContainer();

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private dataBuildsService: DataBuildsService,
    private identity: IdentityService,
    private hostsService: HostsService,
    private teamsService: TeamsService,
    private menuService: MenuService,
    private _socketService: SocketService,
    private readonly _element: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    if (window.innerHeight > 890) {
      this.pageSize = 20;
    }

    if (this.isWidget) {
      this.load(this.teamName);
    } else {
      this.activatedRoute.params.subscribe((params) => {
        this.load(params.team);
      });
    }

    this._socketService.joinRoom('builds');
    this._subs.add = this._socketService.subscribeToRoomEvents('builds', (data) => {
      this.load(this.teamName);
    });

    (<any>window).builds = this;
    this.menuService.delayedSetActive('builds');
  }

  ngOnDestroy(): void {
    this._socketService.leaveRoom('builds');
    this._subs.dispose();
  }

  // Sticky overrides for debugging - set via optimizePageSize({ ... }) and persist across resizes
  // Call resetPageSizeOverrides() to clear them
  public _pageSizeOverrides: { navHeight?: number; omnibarHeight?: number; headerRowHeight?: number; paginatorHeight?: number; rowHeight?: number } = {};

  resetPageSizeOverrides() {
    this._pageSizeOverrides = {};
    this.optimizePageSize();
  }

  private _resizeTimeout: any = null;

  onResize() {
    // Debounce resize - wait for resizing to stop, then allow DOM to settle
    if (this._resizeTimeout) {
      clearTimeout(this._resizeTimeout);
    }
    this._resizeTimeout = setTimeout(() => {
      this._resizeTimeout = null;
      this.optimizePageSize();
    }, 250);
  }

  optimizePageSize(overrides?: { navHeight?: number; omnibarHeight?: number; headerRowHeight?: number; paginatorHeight?: number; rowHeight?: number }) {
    // Merge new overrides with existing sticky overrides
    if (overrides) {
      this._pageSizeOverrides = { ...this._pageSizeOverrides, ...overrides };
    }

    const el = this._element.nativeElement;

    // Measure DOM elements once and cache them. During resize, DOM measurements
    // can be unreliable, but once cached they remain stable.
    if (!this._pageSizeOverrides.omnibarHeight) {
      const omnibar = el.querySelector('app-omnibar') as HTMLElement;
      if (omnibar?.offsetHeight) {
        this._pageSizeOverrides.omnibarHeight = omnibar.offsetHeight;
      }
    }
    if (!this._pageSizeOverrides.headerRowHeight) {
      const headerRow = el.querySelector('.mat-mdc-header-row') as HTMLElement;
      if (headerRow?.offsetHeight) {
        this._pageSizeOverrides.headerRowHeight = headerRow.offsetHeight;
      }
    }
    if (!this._pageSizeOverrides.rowHeight) {
      const dataRow = el.querySelector('.mat-mdc-row') as HTMLElement;
      if (dataRow?.offsetHeight) {
        this._pageSizeOverrides.rowHeight = dataRow.offsetHeight;
      }
    }
    if (!this._pageSizeOverrides.paginatorHeight) {
      const paginatorEl = el.querySelector('mat-paginator') as HTMLElement;
      if (paginatorEl?.offsetHeight) {
        this._pageSizeOverrides.paginatorHeight = paginatorEl.offsetHeight;
      }
    }

    const sticky = this._pageSizeOverrides;

    // Use cached/measured values, with sensible fallbacks
    const omnibarHeight = sticky.omnibarHeight ?? 77;
    const headerRowHeight = sticky.headerRowHeight ?? 45;
    const rowHeight = sticky.rowHeight ?? 40;
    const paginatorHeight = sticky.paginatorHeight ?? 100;
    const navHeight = sticky.navHeight ?? 32;
    const buffer = 20; // Buffer to prevent clipping on partial-row resizes

    let availableHeight: number;
    let totalHeight: number;

    if (this.isWidget) {
      // For widgets, we need to get the gridster cell's constrained height.
      // Problem: if table content overflows, offsetHeight returns the inflated size.
      // Solution: Use the gridster-item's style height which reflects the grid layout.
      const gridsterItem = el.closest('gridster-item') as HTMLElement;
      if (gridsterItem) {
        // Gridster sets explicit height via style attribute during resize
        const styleHeight = gridsterItem.style.height;
        if (styleHeight && styleHeight.endsWith('px')) {
          totalHeight = parseFloat(styleHeight);
        } else {
          totalHeight = gridsterItem.offsetHeight;
        }
      } else {
        totalHeight = el.offsetHeight;
      }
      availableHeight = totalHeight - omnibarHeight - headerRowHeight - paginatorHeight - buffer;
    } else {
      totalHeight = window.innerHeight;
      availableHeight = totalHeight - navHeight - omnibarHeight - headerRowHeight - paginatorHeight - buffer;
    }

    const newPageSize = Math.max(1, Math.floor(availableHeight / rowHeight));

    this.pageSize = newPageSize;

    if (this.paginator) {
      this.paginator._changePageSize(this.pageSize);
    }
  }

  @debounce(2500)
  load(teamName: string) {
    this.teamName = teamName;
    const sub = this.dataBuildsService.load(teamName).subscribe((results: DataBuild[]) => {
      sub.unsubscribe();
      this.isLoading = false;
      const timestampColumns = ['startTime', 'lastModified', 'endTime'];
      timestampColumns.forEach((col) => {
        results.forEach((row) => {
          if (row[col]) {
            row[col] = new Date(row[col]);
          }
        });
      });

      this.originalData = results;
      this.dataSource.data = this.originalData.filter((x) => !x.isArchived);
      this.loadHosts(results.map((e) => e.host));
      setTimeout(() => {
        this.loadView(this.activatedRoute.snapshot.queryParams);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        if (!this.subSort) {
          this.subSort = this.sort?.sortChange.subscribe((value) => {
            this.updateRoute();
          });
        }
      }, 0);
      this.optimizePageSize();
    });
  }

  async loadHosts(hosts: string[]) {
    const uniqueHosts = [...new Set(hosts)];
    await Promise.all(
      uniqueHosts.map(async (hostName: string) => (this.knownHosts[hostName] = await this.hostsService.getAsync(this.teamName, hostName)))
    );
  }

  applyFilter(filterValue: string) {
    if (filterValue.length == 0) this.dataSource.data = this.originalData.filter((x) => !x.isArchived);
    else this.dataSource.data = this.originalData;

    filterValue = filterValue.trim().toLowerCase();
    this.dataSource.filter = filterValue;

    if (this.dataSource.paginator) this.dataSource.paginator.firstPage();

    this.updateRoute();
  }

  openDiff() {
    if (this.isWidget) {
      this.onDiffClick.emit({ left: this.selection.selected[0], right: this.selection.selected[1] });
    } else {
      this.teamsService.getConfigAsync(this.teamName).then((config) => {
        const encode = (x: DataBuild) => encodeURI(`${config.path.logs}/${x.runid}/${x.rdsName}.json`);
        const left = encode(this.selection.selected[0]);
        const right = encode(this.selection.selected[1]);
        window.open(this.router.serializeUrl(this.router.createUrlTree([this.teamName, 'model-diff', left, right])), '_blank');
      });
    }
  }

  updateRoute() {
    const selected = this.selection.selected.map((x) => `${x.host}:${x.rdsName}:${x.runid}`).join(',');
    const queryParams: Params = {};
    if (selected.length > 0) {
      queryParams.selected = selected;
    }

    if (this.sort.active && this.sort.direction && (this.sort.active !== 'lastModified' || this.sort.direction !== 'desc')) {
      queryParams.sort = `${this.sort.active},${this.sort.direction}`;
    }

    if (this.omnibar.value) {
      queryParams.search = this.omnibar.value;
    }

    this.router.navigate([], { relativeTo: this.activatedRoute, queryParams: queryParams });
  }

  loadView(params) {
    if (params.search) {
      this.omnibar.value = params.search;
      this.applyFilter(this.omnibar.value);
    }

    if (params.selected) {
      const selected = params.selected?.split(',');
      selected.forEach((selected) => {
        const [host, rdsName, runid] = selected.split(':');
        const row = this.dataSource.data.find((x) => x.host === host && x.rdsName === rdsName && x.runid === +runid);
        if (row && !this.selection.isSelected(row)) {
          this.selection.toggle(row);
        }
      });
    }

    if (params.sort) {
      const [column, direction] = params.sort.split(',').map((x) => x.trim());
      if (column && direction) this.sort?.sort({ id: column, start: direction } as MatSortable);
    }
  }

  rowClick(row) {
    if (this.isWidget) {
      this.onBuildClick.emit({ rdsName: row.rdsName, runid: row.runid, user: row.user, isArchived: row.isArchived });
    } else {
      this.router.navigate([this.teamName, 'data', 'build', row.rdsName, row.runid, row.user, row.isArchived]);
    }
  }

  getRunningStatus(row: DataBuild): 'Complete' | 'Running' | 'Running_Errors' | 'Errors' | 'Cancelled' | 'Unknown' {
    const { status, errors, cancelled, isRunning } = row;
    if (errors && !isRunning) return 'Errors';
    else if (cancelled) return 'Cancelled';
    else if (isRunning && errors) return 'Running_Errors';
    else if (isRunning) return 'Running';
    else if (status === 'complete') return 'Complete';
    else return 'Unknown';
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.filteredData.length;
    return numSelected === numRows && numRows > 0;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.dataSource.filteredData.forEach((row: DataBuild) => this.selection.select(row));
    }
  }

  private shiftKeyHeld = false;

  @HostListener('document:keydown.shift')
  onShiftDown() { this.shiftKeyHeld = true; }

  @HostListener('document:keyup.shift')
  onShiftUp() { this.shiftKeyHeld = false; }

  onCheckboxChange(event: any, row: DataBuild) {
    if (this.shiftKeyHeld && this.lastClickedRow) {
      // Shift-click: select range between last clicked and current
      this.selectRange(this.lastClickedRow, row);
    } else {
      // Normal click: toggle single row
      this.selection.toggle(row);
    }
    this.lastClickedRow = row;
  }

  private selectRange(fromRow: DataBuild, toRow: DataBuild) {
    const data = this.dataSource.filteredData;
    const fromIndex = data.findIndex((r: DataBuild) => r.runid === fromRow.runid && r.rdsName === fromRow.rdsName);
    const toIndex = data.findIndex((r: DataBuild) => r.runid === toRow.runid && r.rdsName === toRow.rdsName);

    if (fromIndex === -1 || toIndex === -1) return;

    const startIndex = Math.min(fromIndex, toIndex);
    const endIndex = Math.max(fromIndex, toIndex);

    // Select all rows in range including both endpoints
    for (let i = startIndex; i <= endIndex; i++) {
      this.selection.select(data[i]);
    }
  }

  selectAll() {
    this.dataSource.filteredData.forEach((row: DataBuild) => this.selection.select(row));
  }

  refresh() {
    this.isLoading = true;
    this.load(this.teamName);
  }

  allSelectedCanBeArchived() {
    return this.selection.selected.every((x) => (!x.isArchived && x.cancelled) || !x.isRunning);
  }

  allSelectedCanBeRestored() {
    return this.selection.selected.every((x) => x.isArchived);
  }

  allSelectedCanBeStopped() {
    return this.selection.selected.every((x) => x.isRunning);
  }

  archive() {
    this.setArchive(true);
  }

  restore() {
    this.setArchive(false);
  }

  async stop() {
    if (!await this.dialogs.confirm('Are you sure you want to cancel these builds?')) return;

    this.selection.selected.forEach(async (build) => {
      const sub = this.dataBuildsService.stop(this.teamName, build.rdsName, build.runid.toString()).subscribe(() => {
        sub.unsubscribe();
        build.isRunning = false;
        build.cancelled = true;
      });
    });
  }

  add() {
    this.onAddClick.emit();
  }

  clone() {
    const row = this.selection.selected[0];
    this.onCloneClick.emit({ rdsName: row.rdsName, runid: row.runid, isArchived: row.isArchived });
  }

  setArchive(archive: boolean) {
    const archivist = (archive ? this.dataBuildsService.archive : this.dataBuildsService.restore).bind(this.dataBuildsService);
    const { selected } = this.selection;
    const { username } = this.identity.me;

    selected.forEach((build) => {
      const sub = archivist(this.teamName, build.runid, username).subscribe(() => {
        sub.unsubscribe();
        build.isArchived = !build.isArchived;
        this.selection.toggle(build);
        this.applyFilter(this.omnibar.value);
      });
    });
  }
}
